import { useState } from "react";
import { Check, Loader2, Pencil, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { useDeleteItem, useUpdateItem } from "../hooks/useDropdowns";
import type { DropdownItem } from "../types";
import { getMetadataSchema } from "../metadataSchemas";
import { MetadataFields } from "./MetadataFields";

interface DropdownItemRowProps {
  item: DropdownItem;
  /** When non-null, the row is rendered with a Parent Value column. */
  parentItems: DropdownItem[] | null;
}

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={
        active
          ? { background: "hsl(142,55%,92%)", color: "hsl(142,72%,28%)" }
          : { background: "hsl(210,16%,93%)", color: "hsl(215,16%,40%)" }
      }
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function SystemBadge({ isSystem }: { isSystem: boolean }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={
        isSystem
          ? { background: "hsl(210,55%,92%)", color: "hsl(210,100%,28%)" }
          : { background: "hsl(210,16%,93%)", color: "hsl(215,16%,40%)" }
      }
    >
      {isSystem ? "Yes" : "No"}
    </span>
  );
}

export function DropdownItemRow({ item, parentItems }: DropdownItemRowProps) {
  const showParent = parentItems !== null;
  const metadataSchema = getMetadataSchema(item.category);
  const showMetadata = Boolean(metadataSchema);
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(item.label);
  const [parentValue, setParentValue] = useState(item.parentValue ?? "");
  const [metadata, setMetadata] = useState<Record<string, unknown>>(
    item.metadata ?? {},
  );
  const [deleteOpen, setDeleteOpen] = useState(false);

  const update = useUpdateItem();
  const remove = useDeleteItem();

  async function handleSave() {
    if (!label.trim()) return;
    await update.mutateAsync({
      id: item.id,
      payload: {
        label: label.trim(),
        ...(showParent ? { parentValue: parentValue || null } : {}),
        ...(showMetadata ? { metadata } : {}),
      },
    });
    setEditing(false);
  }

  function handleCancel() {
    setLabel(item.label);
    setParentValue(item.parentValue ?? "");
    setMetadata(item.metadata ?? {});
    setEditing(false);
  }

  async function toggleActive() {
    await update.mutateAsync({
      id: item.id,
      payload: { isActive: !item.isActive },
    });
  }

  async function handleDelete() {
    await remove.mutateAsync(item.id);
  }

  return (
    <tr className="hover:bg-[hsl(210,50%,98%)]">
      <td className="px-4 py-3">
        {editing ? (
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleSave();
              if (e.key === "Escape") handleCancel();
            }}
            className="h-8"
          />
        ) : (
          <span className="font-medium text-foreground">{item.label}</span>
        )}
        {showParent && editing && (
          <Select
            value={parentValue}
            onChange={(e) => setParentValue(e.target.value)}
            className="mt-2 h-8"
          >
            <option value="">—</option>
            {parentItems!
              .filter((p) => p.isActive)
              .map((p) => (
                <option key={p.id} value={p.value}>
                  {p.label}
                </option>
              ))}
          </Select>
        )}
        {showMetadata && editing && (
          <div className="mt-3">
            <MetadataFields
              category={item.category}
              value={metadata}
              onChange={setMetadata}
              systemLocked={item.isSystem}
            />
          </div>
        )}
        {showMetadata && !editing && item.metadata && (
          <div className="mt-1 flex flex-wrap gap-1">
            {Object.entries(item.metadata).map(([k, v]) => (
              <span
                key={k}
                className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
                style={{ background: "hsl(210,30%,94%)", color: "hsl(215,20%,35%)" }}
              >
                {k}: {String(v)}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-muted-foreground">
        <code className="rounded bg-[hsl(210,30%,96%)] px-1.5 py-0.5 font-mono text-[11px]">
          {item.value}
        </code>
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground">
        {item.sortOrder}
      </td>
      <td className="px-4 py-3">
        <SystemBadge isSystem={item.isSystem} />
      </td>
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={toggleActive}
          disabled={update.isPending || item.isSystem}
          className="cursor-pointer disabled:cursor-not-allowed"
          aria-label="Toggle active"
        >
          <StatusBadge active={item.isActive} />
        </button>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          {editing ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSave}
                disabled={update.isPending}
                aria-label="Save"
              >
                {update.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                ) : (
                  <Check className="h-4 w-4" strokeWidth={1.5} />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCancel}
                aria-label="Cancel"
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setEditing(true)}
                aria-label="Edit"
              >
                <Pencil className="h-4 w-4" strokeWidth={1.5} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDeleteOpen(true)}
                disabled={item.isSystem}
                aria-label="Delete"
              >
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </Button>
            </>
          )}
        </div>
        <ConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete dropdown value?"
          description={`Are you sure you want to delete the value '${item.label}'? Any records using this value will need to be reassigned.`}
          onConfirm={handleDelete}
        />
      </td>
    </tr>
  );
}
